'use client';

import clsx from 'clsx';
import Image from 'next/image';
import { useTranslations } from 'next-intl';

import { BRAND_NAME } from '@/lib/const';
import type { RepairShippingKitOption } from '@/lib/service/ecs/logisticsCapabilities';

import styles from './RepairRequestShippingKitSelector.module.scss';

interface RepairRequestShippingKitSelectorProps {
  selected: RepairShippingKitOption;
  onSelect?: (option: RepairShippingKitOption) => void;
  showBrandKit: boolean;
  readOnly?: boolean;
}

export function RepairRequestShippingKitSelector({
  selected,
  onSelect,
  showBrandKit,
  readOnly = false,
}: Readonly<RepairRequestShippingKitSelectorProps>) {
  const t = useTranslations('service.repairRequest.personal.shippingKit');
  const brand = BRAND_NAME;

  const renderCard = (
    option: RepairShippingKitOption,
    testId: string,
    imageSrc: string,
    title: string,
    subtitle: string
  ) => {
    const isSelected = selected === option;

    if (readOnly && !isSelected) {
      return null;
    }

    const cardClassName = clsx(styles.card, {
      [styles.cardSelected]: isSelected && !readOnly,
      [styles.cardReadOnly]: readOnly,
    });

    if (readOnly) {
      return (
        <div className={cardClassName} data-testid={testId}>
          <Image src={imageSrc} alt="" width={54} height={54} className={styles.image} />
          <div className={styles.copy}>
            <p className={styles.title}>{title}</p>
            <p className={styles.subtitle}>{subtitle}</p>
          </div>
        </div>
      );
    }

    return (
      <button
        type="button"
        className={cardClassName}
        onClick={() => onSelect?.(option)}
        data-testid={testId}
      >
        <Image src={imageSrc} alt="" width={54} height={54} className={styles.image} />
        <div className={styles.copy}>
          <p className={styles.title}>{title}</p>
          <p className={styles.subtitle}>{subtitle}</p>
        </div>
      </button>
    );
  };

  return (
    <div className={styles.grid}>
      {showBrandKit
        ? renderCard(
            'BreitlingSending',
            'personal-shipping-kit-brand',
            '/images/service/brandBox.png',
            t('brandKitTitle', { brand }),
            t('brandKitSubtitle')
          )
        : null}
      {renderCard(
        'ClientSending',
        'personal-shipping-kit-own',
        '/images/service/standardBox.png',
        t('ownPackagingTitle'),
        t('ownPackagingSubtitle')
      )}
    </div>
  );
}
